import React, { useState, useEffect } from "react";

import {
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  VStack,
  Tag,
  Text,
  HStack,
  useQuery,
  Button,
  Badge,
  Box,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiDollarSign } from "react-icons/fi";
import { Payment, User } from "types";

export const PaymentsTable = ({
  payments,
  page,
  totalPages,
  setPage,
}: {
  payments: Payment[];
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
}) => {
  const { t, i18n } = useTranslation();
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === "pt-BR" ? "pt-BR" : "en-US"
    );
  };
  const getPaymentMethod = (paymentMethod: string) => {
    switch (paymentMethod) {
      case "efipay-card":
        return "Cartão";
      case "efipay-billet":
        return "Boleto";
      case "efipay-pix":
        return "Pix";
      case "efipay-link":
        return "Link";
      default:
        return "---";
    }
  };
  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: currency,
    }).format(amount);
  };
  const getStatusColor = (status: Payment["status"]) => {
    switch (status) {
      case "PAID":
        return "green";
      case "PENDING":
        return "yellow";
      case "FAILED":
        return "red";
      case "CANCELLED":
        return "gray";
      default:
        return "gray";
    }
  };
  const handlePendingPayment = (payment: Payment) => {
    if (payment.paymentUrl) {
      window.location.href = payment.paymentUrl;
    }
  };
  return (
    <Box>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>{t("payments.history.client")}</Th>
            <Th>{t("payments.history.date")}</Th>
            <Th>{t("payments.history.description")}</Th>
            <Th>{t("payments.history.type")}</Th>
            <Th>{t("payments.history.amount")}</Th>
            <Th>{t("payments.history.status")}</Th>
            <Th>{t("payments.history.actions")}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {payments &&
            payments.map((payment) => (
              <Tr key={payment.id}>
                <Td>
                  <VStack align="flex-start">
                    <Tag size="sm" colorScheme="gray">
                      {payment.user?.email}
                    </Tag>
                    <Tag size="sm" colorScheme="gray">
                      {payment.user?.cpf}
                    </Tag>
                  </VStack>
                </Td>
                <Td>{formatDate(payment.createdAt)}</Td>
                <Td>
                  <Text fontSize="sm">{payment.description} </Text>
                  <Tag colorScheme="teal">
                    {getPaymentMethod(payment.paymentMethod!)}
                  </Tag>
                </Td>
                <Td>
                  <Badge colorScheme="purple">
                    {t(`payments.types.${payment.type.toLowerCase()}`)}
                  </Badge>
                </Td>
                <Td>{formatAmount(payment.amount, payment.currency)}</Td>
                <Td>
                  <Badge colorScheme={getStatusColor(payment.status)}>
                    {t(`payments.status.${payment.status.toLowerCase()}`)}
                  </Badge>
                </Td>
                <Td>
                  {payment.status === "PENDING" && (
                    <HStack spacing={2}>
                      {payment.paymentUrl && (
                        <Button
                          size="sm"
                          leftIcon={<FiDollarSign />}
                          colorScheme="yellow"
                          onClick={() => handlePendingPayment(payment)}
                        >
                          {t("payments.pending.payNow")}
                        </Button>
                      )}
                    </HStack>
                  )}
                </Td>
              </Tr>
            ))}
        </Tbody>
      </Table>
      {/* Add pagination controls */}
      <HStack spacing={2} justify="space-between" w="full">
        <Button
          onClick={() => setPage(Math.max(1, page - 1))}
          isDisabled={page === 1}
        >
          {t("common.previous")}
        </Button>
        <Text>
          {t("common.page")} {page} / {totalPages}
        </Text>
        <Button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          isDisabled={page === totalPages}
        >
          {t("common.next")}
        </Button>
      </HStack>
    </Box>
  );
};
